import { Button, Card, Text, VSpacer } from '@/components/DesignSystem';
import { getReservedBalance } from '@/components/shared/Booking/helper';
import { PointsRemainingChip } from '@/components/shared/PointsRemainingChip';
import { Routes } from '@/constants/Routes';
import { useAuthentication } from '@/contexts/dataSync/AuthenticationContext';
import { useDashboardNavigate } from '@/pages/CustomerDashboard/helpers';
import { ApiOrderTransaction, ApiSubwalletBalance, ApiUser } from '@api/interfaces';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { Divider, Stack, useTheme } from '@mui/material';
import { getAccountName } from '@shared/utilities';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

interface BookingsListProps {
  balances: ApiSubwalletBalance[],
  bookings: ApiOrderTransaction[],
  brandColor?: string,
  customer: ApiUser,
  linkedAccounts?: ApiUser[],
}

export const BookingsList = ({
  balances,
  bookings,
  brandColor,
  customer,
  linkedAccounts,
}: BookingsListProps) => {
  const { t } = useTranslation();
  const { isInternal } = useAuthentication();
  const navigate = useNavigate();
  const theme = useTheme();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get('accessToken') ?? params.accessToken;
  const navigateToPage = useDashboardNavigate();

  const showAccountName = (booking: ApiOrderTransaction) => {
    const hasLinkedAccounts = !!(linkedAccounts && linkedAccounts.length > 0);
    const accountName = booking.farmer ? getAccountName(booking.farmer) : '';
    return hasLinkedAccounts && accountName !== getAccountName(customer);
  };

  const handleGoToBooking = (booking: ApiOrderTransaction) => {
    if (accessToken) {
      navigateToPage(
        Routes.CUSTOMER_DASHBOARD_BOOKING_DETAIL,
        accessToken,
        { bookingId: booking.id },
        { returnToWallet: true },
      );
    } else {
      const urlParams = new URLSearchParams();
      if (isInternal) {
        urlParams.set('retailerId', booking.retailerId);
      }
      urlParams.set('bookingExternalId', booking.groupingId);
      navigate(`${Routes.ADMIN_BOOKINGS}?${urlParams.toString()}`);
    }
  };

  return (
    <Stack spacing={2}>
      {bookings.map((booking) => (
        <Fragment key={booking.id}>
          <Divider sx={{ marginTop: '0px !important' }} />
          <Card
            cardContentStyle={{
              paddingX: '0px',
            }}
            elevation={0}
            onClick={(e) => {
              e.stopPropagation();
              handleGoToBooking(booking);
            }}
            sx={{
              border: 'none !important',
              cursor: 'pointer',
              marginTop: '0px !important',
            }}
            testID={`booking-item-${booking.groupingId}`}
          >
            <Stack alignItems="center" direction="row" justifyContent="space-between">
              <Stack flex={1}>
                <Text breakWord category="title-small">
                  {t($ => $.orders.details.booking, { bookingId: booking.groupingId })}
                </Text>
                {showAccountName(booking) && (
                  <>
                    <VSpacer size="1" />
                    <Text category="body-small" sx={{ color: theme.palette.grey[700] }}>
                      {booking.farmer && getAccountName(booking.farmer)}
                    </Text>
                  </>
                )}
                <VSpacer size="2" />
                <PointsRemainingChip
                  text={
                    t($ => $.common.booking.pointsRemaining, {
                      count: getReservedBalance(booking, balances),
                    })
                  }
                />
              </Stack>
              <Button
                endIcon={<ArrowForward sx={{ marginLeft: '-8px' }}/>}
                size="small"
                sx={{ color: brandColor ?? theme.palette.primary.main }}
                testID={`go-to-booking-button-${booking.id}`}
                variant="text"
              >
                {t($ => $.common.booking.goToBooking)}
              </Button>
            </Stack>
          </Card>
        </Fragment>
      ))}
    </Stack>
  );
};
